import { watch, computed, nextTick } from 'vue';

import { useActiveUrlStore } from '@/store/activeUrlStore';
import { useUrlsStore } from '@/store/urlsStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useSettingsStore } from '@/store/settings/settingsStore';

import { buildOrderedQueue } from '@/utils/urlQueue';
import { processSingleUrlFetch } from '@/services/processSingleUrlFetch';

import { GoogleAnalytics } from '@/services/analytics/googleAnalytics';

export default function useFetchUrlsSources() {
  /* ------------------------------------------------------------------ */
  /* stores & reactive refs                                              */
  /* ------------------------------------------------------------------ */
  // Store references for managing active URL, sitemap URLs, notifications, and settings
  const activeUrl = useActiveUrlStore();
  const urlsStore = useUrlsStore();
  const notification = useNotificationStore();
  const settings = useSettingsStore();

  // Computed property to get the list of sitemap URLs from the store
  const sitemapUrls = computed(() => urlsStore.urls);

  /* ------------------------------------------------------------------ */
  /* batch-run bookkeeping                                               */
  /* ------------------------------------------------------------------ */
  // Flags and tokens to manage the batch processing state
  let isProcessing = false; // Indicates if a batch is currently being processed
  let rerunQueued = false;  // Indicates if a rerun is queued
  let runToken = 0;         // Token to track the current batch run

  // Queue a rerun if a batch is already processing, otherwise trigger a new run
  const queueRerun = () =>
    isProcessing ? (rerunQueued = true) : triggerRun();

  // Trigger a new batch run
  const triggerRun = async () => {
    isProcessing = true; // Mark as processing
    runToken += 1;       // Increment the run token
    const myToken = runToken; // Capture the current token for this run
    await processBatch(myToken); // Process the batch
    isProcessing = false; // Mark as not processing

    // If a rerun was queued, trigger it after the current run completes
    if (rerunQueued) {
      rerunQueued = false;
      await nextTick(); // Wait for the next tick to avoid race conditions
      triggerRun();     // Trigger the rerun
    }
  };

  /* ------------------------------------------------------------------ */
  /* core batch                                                          */
  /* ------------------------------------------------------------------ */
  // Process a batch of sitemap URLs
  const processBatch = async (token: number) => {
    const baseUrl = activeUrl.activeUrl; // Get the active base URL
    if (!baseUrl) return; // Exit if no active URL is set

    /* --- build queue ------------------------------------------------- */
    // Select pending sitemap URLs to process
    const pending = urlsStore.getPendingUrlsToFetch();
    if (!pending.length) return; // Exit if no pending URLs

    // Build an ordered queue of URLs to process
    const ordered = buildOrderedQueue(pending);

    try {
      // Process each URL in the ordered queue
      for (const url of ordered) {
        if (token !== runToken) return; // Exit if the site switched during processing

        urlsStore.markFetchInProgress(url.pathname); // Mark the URL as in progress

        await processSingleUrlFetch(url); // Fetch and process the URL

        if (token !== runToken) return; // Exit if the site switched mid-await
        // Note: processSingleUrlFetch already sets the final fetch status
      }

      /* batch finished successfully */

      if (token === runToken) {
        GoogleAnalytics.scrapeSuccessful(); // Log success to Google Analytics
        if (settings.getIsDebugMode)
          notification.addSuccessNotification('Fetched All URLs.'); // Notify success
      }
    } catch (err) {
      // Handle errors during batch processing
      if (token === runToken)
        notification.addErrorNotification(`Error fetching sitemap URLs: ${err}`); // Notify error
    }
  };

  /* ------------------------------------------------------------------ */
  /* watchers                                                            */
  /* ------------------------------------------------------------------ */
  // Watch for changes in the sitemap URLs list and queue a rerun
  watch(sitemapUrls, queueRerun, { immediate: true, deep: true });

  // Watch for changes in the active site URL
  watch(
    () => activeUrl.activeUrl,
    () => {
      runToken += 1;  // Cancel the current batch by incrementing the token
      queueRerun();   // Queue a fresh batch run
    },
  );
}
